import { createContext, useContext, useMemo } from 'react';
import type { JSX, ReactNode } from 'react';
import { applyPiping, type PipingContext as PipingCtx } from '../utils/piping.js';

const Context = createContext<PipingCtx>({ responses: {}, variables: {} });

interface PipingProviderProps {
  responses: Record<string, unknown>;
  variables: Record<string, unknown>;
  children: ReactNode;
}

// React context wrapper so deep components (PageHeader, QuestionCard,
// WelcomePage, ThankYouView) can apply piping without prop-drilling the
// responses + variables maps through every layer.
export function PipingProvider({ responses, variables, children }: PipingProviderProps): JSX.Element {
  const value = useMemo<PipingCtx>(() => ({ responses, variables }), [responses, variables]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

// Read-only hook returning a piping function. Components call `pipe(text)`
// to transform survey-author text. Cheap — short-circuits when text has no
// `{{` so the common case is a no-op.
export function usePipe(): (text: string | undefined | null) => string {
  const ctx = useContext(Context);
  return (text) => applyPiping(text, ctx);
}
